import { joinURL } from 'ufo';
import { useSystemFetch } from '#layers/base/public';

/**
 * API functions for managing service account workspace assignments.
 *
 * Uses dedicated endpoints:
 * - POST /{workspace}/service-accounts - add service account to workspace
 * - DELETE /{workspace}/members - remove service account from workspace (service accounts are workspace members)
 *
 * Operates on caller-named workspaces through the system transport, so the target is never the
 * active workspace by default.
 */

interface AddServiceAccountPayload {
	serviceAccountId: string;
	roles?: string[];
}

interface RemoveWorkspaceMemberPayload {
	userId: string;
}

/**
 * Add a service account to a workspace.
 *
 * @param workspaceSlug - The workspace slug/apiName
 * @param serviceAccountId - The service account ID to add
 * @param roles - Optional array of role IDs to assign
 */
export async function addServiceAccountToWorkspace(
	workspaceSlug: string,
	serviceAccountId: string,
	roles: string[] = [],
): Promise<void> {
	const fetch = useSystemFetch();
	const payload: AddServiceAccountPayload = {
		serviceAccountId,
		roles,
	};

	await fetch(joinURL(workspaceSlug, 'service-accounts'), {
		method: 'POST',
		body: payload,
	});
}

/**
 * Remove a service account from a workspace.
 *
 * @param workspaceSlug - The workspace slug/apiName
 * @param serviceAccountId - The service account ID to remove
 */
export async function removeServiceAccountFromWorkspace(
	workspaceSlug: string,
	serviceAccountId: string,
): Promise<void> {
	const fetch = useSystemFetch();
	const payload: RemoveWorkspaceMemberPayload[] = [{ userId: serviceAccountId }];

	await fetch(joinURL(workspaceSlug, 'members'), {
		method: 'DELETE',
		body: payload,
	});
}
